import React, { useState } from 'react';
import { Table, TableHead, TableBody, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { getSmsSenderList, approveSmsSender, deleteSmsSender } from '../../../api/sms';
import { formatLandlinePhone } from '../../../utils/methods';
import {
  TableControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchGroup,
  SearchInput,
  SearchButton,
  HeaderCell,
  BodyCell,
  DataRow,
  StatusText,
  ActionGroup,
  VerifyButton,
  DeleteButton,
  PaginationContainer,
  PageButton,
  NavButton,
} from './SmsSenderNumberStyled';

const COLUMNS = [
  { id: 'branchName', label: '지점명', width: 140 },
  { id: 'regDate', label: '신청일시', width: 160 },
  { id: 'senderName', label: '발신자명', width: 200 },
  { id: 'senderNumber', label: '발신번호', width: 180 },
  { id: 'status', label: '인증여부', width: 120 },
  { id: 'action', label: '기타', width: 160 },
];

const STATUS_LABEL = {
  VERIFIED: '인증완료',
  UNDER_REVIEW: '인증요청',
  UNVERIFIED: '미인증',
};

const formatDateTime = value => {
  if (!value) return '-';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : '-';
};

const formatPhone = number => {
  if (!number) return '-';
  const digits = String(number).replace(/[^0-9]/g, '');
  // 대표번호(1588-XXXX 등) 8자리는 4-4
  if (digits.length === 8) return digits.replace(/(\d{4})(\d{4})/, '$1-$2');
  // 휴대폰/서울(02)/지역 자동 포맷 (공용 헬퍼)
  return formatLandlinePhone(digits) || '-';
};

const SmsSenderNumber = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const query = QueryString.stringify(
    {
      page: page - 1,
      size: pageSize,
      keyword: searchKeyword || undefined,
    },
    { skipNulls: true },
  );

  const { data: senderData } = useQuery({
    queryKey: ['smsSenders', page, pageSize, searchKeyword],
    queryFn: () => getSmsSenderList(query, user?.token),
    enabled: !!user?.token,
  });

  const senders = senderData?.content || [];
  const totalPages = senderData?.totalPages || 1;

  const approveMutation = useMutation({
    mutationFn: id => approveSmsSender(id, user?.token),
    onSuccess: () => {
      toast.success(t('승인되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['smsSenders'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: sender => deleteSmsSender(sender.branchId, sender.id, user?.token),
    onSuccess: () => {
      toast.success(t('삭제되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['smsSenders'] });
    },
  });

  const handleSearch = () => {
    setSearchKeyword(searchValue);
    setPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleApprove = sender => {
    if (window.confirm(t('해당 발신번호를 승인하시겠습니까?'))) {
      approveMutation.mutate(sender.id);
    }
  };

  const handleDelete = sender => {
    if (window.confirm(t('해당 발신번호를 삭제하시겠습니까?'))) {
      deleteMutation.mutate(sender);
    }
  };

  const renderCellValue = (sender, columnId) => {
    switch (columnId) {
      case 'branchName':
        return sender.branchName || '-';
      case 'regDate':
        return formatDateTime(sender.regDate);
      case 'senderName':
        return sender.senderName || '-';
      case 'senderNumber':
        return formatPhone(sender.senderNumber);
      case 'status':
        return <StatusText status={sender.status}>{t(STATUS_LABEL[sender.status] || sender.status)}</StatusText>;
      case 'action':
        return (
          <ActionGroup>
            <VerifyButton
              onClick={() => handleApprove(sender)}
              disabled={sender.status === 'VERIFIED' || approveMutation.isPending}>
              {t('승인')}
            </VerifyButton>
            <DeleteButton onClick={() => handleDelete(sender)} disabled={deleteMutation.isPending}>
              {t('삭제')}
            </DeleteButton>
          </ActionGroup>
        );
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {endPage < totalPages && (
          <>
            <PageButton disabled>...</PageButton>
            <PageButton onClick={() => setPage(totalPages)}>{totalPages}</PageButton>
          </>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <TableControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchGroup>
          <SearchInput
            placeholder={t('발신자명 또는 발신번호 검색')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={handleSearch}>{t('검색')}</SearchButton>
        </SearchGroup>
      </TableControlsRow>

      <Table>
        <TableHead>
          <DataRow>
            {COLUMNS.map(col => (
              <HeaderCell key={col.id} style={{ width: col.width || 'auto' }}>
                {t(col.label)}
              </HeaderCell>
            ))}
          </DataRow>
        </TableHead>
        <TableBody>
          {senders.length > 0 ? (
            senders.map((sender, idx) => (
              <DataRow key={sender.id || idx}>
                {COLUMNS.map(col => (
                  <BodyCell key={col.id} style={{ width: col.width || 'auto' }}>
                    {renderCellValue(sender, col.id)}
                  </BodyCell>
                ))}
              </DataRow>
            ))
          ) : (
            <DataRow>
              <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                {t('데이터가 없습니다')}
              </BodyCell>
            </DataRow>
          )}
        </TableBody>
      </Table>

      {renderPagination()}
    </>
  );
};

export default SmsSenderNumber;
